import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Checkbox,
  ListItemText,
  InputAdornment,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Add, PlayArrow, PersonAdd, StopCircle, Delete as DeleteIcon, Edit as EditIcon, Visibility as VisibilityIcon, Download, Search as SearchIcon, Clear as ClearIcon } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import specialSessionService from "../../services/specialSessionService";
import {
  formatLectureScheduleStatus,
  isSpecialSessionAttendanceRunning,
} from "../../utils/lectureScheduleStatus";
import {
  formatDateTimeIST,
  parseDateTimeLocalAsIST,
  utcIsoToDatetimeLocalIST,
} from "../../utils/timetableTimeSlots";
import departmentService from "../../services/departmentService";
import classService from "../../services/classService";
import QRCode from "react-qr-code";

const isValidEmail = (value) => {
  const v = String(value || "").trim();
  if (!v) return true; // optional
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
};

const isValidPhone = (value) => {
  const v = String(value || "").trim();
  if (!v) return true; // optional
  const digits = v.replace(/[^\d]/g, "");
  return digits.length >= 7 && digits.length <= 15;
};

const SESSION_TYPES = [
  "Regular Lecture",
  "Event",
  "Workshop",
  "Seminar",
  "Guest Lecture",
  "Training Session",
  "Special Session",
  "Activity",
  "Custom Type",
];

const isPresetSessionType = (type) => SESSION_TYPES.includes(type) && type !== "Custom Type";

const resolveSessionTypeForm = (storedType) => {
  if (!storedType) return { sessionType: "Special Session", customSessionType: "" };
  if (isPresetSessionType(storedType)) return { sessionType: storedType, customSessionType: "" };
  return { sessionType: "Custom Type", customSessionType: storedType };
};

const buildSessionTypePayload = (form) =>
  form.sessionType === "Custom Type" ? String(form.customSessionType || "").trim() : form.sessionType;

const formatDepartmentLabel = (d) => d?.departmentName || d?.name || (d?.id != null ? `Department ${d.id}` : "");

const formatClassLabel = (c) => {
  const name = c?.className || c?.name || (c?.id != null ? `Class ${c.id}` : "");
  const section = c?.section || c?.classSection || c?.sectionName || "";
  const deptCode = c?.department?.code || "";
  const deptName = c?.department?.name || c?.departmentName || "";
  const deptLabel = deptCode || deptName;
  const classBase = section ? `${name} - ${section}` : name;
  return deptLabel ? `${deptLabel} - ${classBase}` : classBase;
};

const SpecialSessionsManagement = () => {
  const navigate = useNavigate();
  // user kept for future session detail screens; not used in this simplified form.
  useSelector((state) => state.auth?.user);

  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [dateFilter, setDateFilter] = useState(null);

  const [departments, setDepartments] = useState([]);
  const [classes, setClasses] = useState([]);
  const [lookupsLoading, setLookupsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  // Single-department scope for the Create Session audience class picker (Req 4.1-4.3).
  // Narrows the Classes options to one department so admins/super-admins are not shown
  // an unbounded all-department class list. Empty string = no scope (show all classes).
  const [classDeptScope, setClassDeptScope] = useState("");

  const [createOpen, setCreateOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [addExternalOpen, setAddExternalOpen] = useState(false);
  const [activeSessionForExternal, setActiveSessionForExternal] = useState(null);
  const [startModalOpen, setStartModalOpen] = useState(false);
  const [startModalData, setStartModalData] = useState(null);
  const [modesDialogOpen, setModesDialogOpen] = useState(false);
  const [modesDialogItems, setModesDialogItems] = useState([]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [error, setError] = useState(null);

  const [form, setForm] = useState({
    title: "",
    sessionType: "Special Session",
    customSessionType: "",
    description: "",
    hostType: "internal",
    hostName: "",
    departmentIds: [],
    classIds: [],
    allowExternalParticipants: false,
    startDateTime: "",
    endDateTime: "",
    venue: "",
    sessionMode: "offline",
    qrEnabled: true,
    otpEnabled: false,
    manualEnabled: true,
    attendanceRequired: true,
  });

  const [externalForm, setExternalForm] = useState({
    attendeeName: "",
    attendeeEmail: "",
    attendeePhone: "",
    attendeeCompany: "",
    attendeeDesignation: "",
    attendeeNotes: "",
    attendeeType: "outsider",
  });
  const [externalErrors, setExternalErrors] = useState({ attendeeEmail: "", attendeePhone: "" });

  const openEdit = (row) => {
    setSelectedSession(row);
    const typeFields = resolveSessionTypeForm(row?.sessionType);
    setForm((p) => ({
      ...p,
      title: row?.title || "",
      sessionType: typeFields.sessionType,
      customSessionType: typeFields.customSessionType,
      description: row?.description || "",
      hostType: row?.hostType || "internal",
      hostName: row?.hostName || "",
      departmentIds: Array.isArray(row?.departmentIds) ? row.departmentIds : [],
      classIds: Array.isArray(row?.classIds) ? row.classIds : [],
      allowExternalParticipants: !!row?.allowExternalParticipants,
      startDateTime: utcIsoToDatetimeLocalIST(row?.startDateTime),
      endDateTime: utcIsoToDatetimeLocalIST(row?.endDateTime),
      venue: row?.venue || "",
      qrEnabled: !!row?.qrEnabled,
      otpEnabled: !!row?.otpEnabled,
      manualEnabled: row?.manualEnabled !== false,
      attendanceRequired: row?.attendanceRequired !== false,
    }));
    setEditOpen(true);
  };

  const openDelete = (row) => {
    setSelectedSession(row);
    setDeleteOpen(true);
  };

  const loadLookups = async () => {
    setLookupsLoading(true);
    try {
      const [depRes, classRes] = await Promise.all([
        departmentService.getAllDepartments(),
        classService.getAllClasses({ page: 1, limit: 200, scope: "institute" }),
      ]);

      const depRows = depRes?.data?.data ?? depRes?.data ?? [];
      setDepartments(Array.isArray(depRows) ? depRows : []);

      const clsRows = classRes?.data?.data?.rows ?? classRes?.data?.rows ?? classRes?.rows ?? classRes?.data ?? [];
      setClasses(Array.isArray(clsRows) ? clsRows : []);
    } catch (e) {
      // lookups are best-effort; page can still be used without audience filters
      console.warn("SpecialSessions lookups failed:", e?.message || e);
    } finally {
      setLookupsLoading(false);
    }
  };

  const validateSessionForm = (values) => {
    const errors = {};
    if (!String(values.title || "").trim()) errors.title = "Title is required";
    if (!values.startDateTime) errors.startDateTime = "Start date/time is required";
    if (!values.endDateTime) errors.endDateTime = "End date/time is required";
    if (values.startDateTime) {
      const year = Number(String(values.startDateTime).slice(0, 4));
      if (!Number.isFinite(year) || year < 1900 || year > 2100) {
        errors.startDateTime = "Enter a valid start year";
      }
    }
    if (values.endDateTime) {
      const year = Number(String(values.endDateTime).slice(0, 4));
      if (!Number.isFinite(year) || year < 1900 || year > 2100) {
        errors.endDateTime = "Enter a valid end year";
      }
    }
    if (values.startDateTime && values.endDateTime) {
      const startAt = parseDateTimeLocalAsIST(values.startDateTime);
      const endAt = parseDateTimeLocalAsIST(values.endDateTime);
      if (startAt && endAt && endAt.getTime() <= startAt.getTime()) {
        errors.endDateTime = "End must be after start";
      }
    }
    if (!values.qrEnabled && !values.otpEnabled && !values.manualEnabled) {
      errors.modes = "Enable at least one attendance mode";
    }
    if (values.hostType === "external" && !String(values.hostName || "").trim()) {
      errors.hostName = "Host name is required for external sessions";
    }
    if (values.sessionType === "Custom Type" && !String(values.customSessionType || "").trim()) {
      errors.customSessionType = "Enter a custom session type name";
    }
    return errors;
  };

  const loadSessions = async (nextPage = page, nextPageSize = pageSize) => {
    setLoading(true);
    setError(null);
    try {
      const params = { limit: nextPageSize, offset: nextPage * nextPageSize };
      if (statusFilter) params.status = statusFilter;
      if (dateFilter) {
        const d = new Date(dateFilter);
        if (Number.isFinite(d.getTime())) {
          params.date = d.toISOString().slice(0, 10);
        }
      }
      const res = await specialSessionService.list(params);
      const payload = res?.data?.data ?? {};
      let list = payload?.rows ?? [];
      // Client-side text search (title) and type filter (backend doesn't support these)
      if (searchQuery.trim()) {
        const q = searchQuery.trim().toLowerCase();
        list = list.filter((r) => (r.title || "").toLowerCase().includes(q));
      }
      if (typeFilter) {
        list = list.filter((r) => r.sessionType === typeFilter);
      }
      setRows(Array.isArray(list) ? list : []);
      setTotal(searchQuery.trim() || typeFilter ? list.length : (Number(payload?.count) || (Array.isArray(list) ? list.length : 0)));
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to load sessions");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // When departments are selected, filter classes to show only those in selected departments.
  // For special sessions (special-case events), all institute classes are fetched but
  // narrowed by department selection for usability.
  const filteredClasses = useMemo(() => {
    const list = Array.isArray(classes) ? classes : [];
    // Single-department scope (Create Session dropdown) takes precedence: narrow to one dept.
    if (classDeptScope !== "" && classDeptScope != null) {
      return list.filter((c) => {
        const classDeptId = c?.departmentId || c?.department?.id;
        return Number(classDeptId) === Number(classDeptScope);
      });
    }
    // Fallback: honor the multi-select department audience field if used.
    if (!form.departmentIds || form.departmentIds.length === 0) return list;
    return list.filter((c) => {
      const classDeptId = c?.departmentId || c?.department?.id;
      return form.departmentIds.some((dId) => Number(dId) === Number(classDeptId));
    });
  }, [classes, form.departmentIds, classDeptScope]);

  useEffect(() => {
    loadSessions(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, statusFilter, typeFilter, dateFilter, searchQuery]);

  // Live attendance counts for running sessions (polls every 5s)
  const [liveCounts, setLiveCounts] = useState({});
  useEffect(() => {
    const runningSessions = rows.filter((r) => r.attendanceStarted === true);
    if (runningSessions.length === 0) {
      setLiveCounts({});
      return;
    }
    let active = true;
    const fetchCounts = async () => {
      const counts = {};
      await Promise.all(
        runningSessions.map(async (s) => {
          try {
            const res = await specialSessionService.getAttendeeCount(s.id);
            const data = res?.data?.data ?? {};
            counts[s.id] = { marked: data.marked || 0, total: data.total || 0 };
          } catch { /* ignore */ }
        })
      );
      if (active) setLiveCounts(counts);
    };
    fetchCounts();
    const interval = setInterval(fetchCounts, 5000);
    return () => { active = false; clearInterval(interval); };
  }, [rows]);

  const columns = useMemo(
    () => [
      { field: "id", headerName: "ID", width: 80 },
      { field: "title", headerName: "Title", flex: 1, minWidth: 200 },
      { field: "sessionType", headerName: "Type", width: 160 },
      {
        field: "status",
        headerName: "Status",
        width: 200,
        renderCell: (p) => {
          const label = formatLectureScheduleStatus(p?.row?.status);
          const count = liveCounts[p?.row?.id];
          if (p?.row?.attendanceStarted && count) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Chip size="small" label={label} color="success" variant="outlined" />
                <Typography variant="caption" fontWeight={600} color="success.main">
                  {count.marked}/{count.total}
                </Typography>
              </Box>
            );
          }
          return <Chip size="small" label={label} variant="outlined" />;
        },
      },
      {
        field: "startDateTime",
        headerName: "Start",
        width: 190,
        valueGetter: (p) => formatDateTimeIST(p?.row?.startDateTime),
      },
      {
        field: "endDateTime",
        headerName: "End",
        width: 190,
        valueGetter: (p) => formatDateTimeIST(p?.row?.endDateTime),
      },
      {
        field: "modes",
        headerName: "Modes",
        width: 160,
        sortable: false,
        renderCell: (p) => {
          const tags = [];
          if (p?.row?.qrEnabled) tags.push("QR");
          if (p?.row?.otpEnabled) tags.push("OTP");
          if (p?.row?.manualEnabled) tags.push("Manual");
          if (p?.row?.allowExternalParticipants) tags.push("External");
          tags.push(p?.row?.attendanceRequired ? "Required" : "Optional");

          const visible = tags.slice(0, 2);
          const remaining = tags.length - visible.length;

          return (
            <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", overflow: "hidden" }}>
              {visible.map((t) => (
                <Chip key={t} size="small" label={t} />
              ))}
              {remaining > 0 ? (
                <Chip
                  size="small"
                  label={`+${remaining}`}
                  variant="outlined"
                  onClick={() => {
                    setModesDialogItems(tags);
                    setModesDialogOpen(true);
                  }}
                  sx={{ cursor: "pointer" }}
                />
              ) : null}
            </Box>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        width: 260,
        sortable: false,
        renderCell: (p) => (
          <Box sx={{ display: "flex", gap: 0.5, alignItems: "center" }}>
            <Tooltip title="View details">
              <IconButton size="small" onClick={() => navigate(`${p.row.id}`)}>
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Download attendance">
              <IconButton
                size="small"
                color="success"
                onClick={async () => {
                  try {
                    const res = await specialSessionService.exportAttendees(p.row.id);
                    const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
                    const url = window.URL.createObjectURL(blob);
                    const a = document.createElement("a");
                    a.href = url;
                    a.download = `${(p.row.title || "session").replace(/[^a-zA-Z0-9]/g, "_")}_attendees.xlsx`;
                    document.body.appendChild(a);
                    a.click();
                    a.remove();
                    window.URL.revokeObjectURL(url);
                  } catch (e) {
                    setSnackbar({ open: true, message: e?.response?.data?.message || "Failed to download", severity: "error" });
                  }
                }}
              >
                <Download fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title={isSpecialSessionAttendanceRunning(p?.row) ? "Attendance already started" : "Start attendance"}>
              <span>
              <IconButton
                size="small"
                color="primary"
                disabled={
                  isSpecialSessionAttendanceRunning(p?.row) ||
                  ["UPCOMING", "COMPLETED"].includes(String(p?.row?.status || "").toUpperCase()) ||
                  (String(p?.row?.status || "").toUpperCase() === "NOT_TAKEN" &&
                    Number.isFinite(new Date(p?.row?.endDateTime).getTime()) &&
                    new Date(p?.row?.endDateTime).getTime() <= Date.now())
                }
                onClick={async () => {
                  try {
                    const resp = await specialSessionService.start(p.row.id);
                    const data = resp?.data?.data ?? {};
                    setStartModalData({
                      sessionId: p.row.id,
                      title: p.row.title,
                      qrPublicId: data.qrPublicId || null,
                      otpCode: data.otpCode || null,
                      otpExpiresAt: data.otpExpiresAt || null,
                    });
                    setStartModalOpen(true);
                    setSnackbar({ open: true, message: "Session started", severity: "success" });
                    loadSessions();
                  } catch (e) {
                    setSnackbar({ open: true, message: e?.response?.data?.message || "Failed to start", severity: "error" });
                  }
                }}
              >
                <PlayArrow fontSize="small" />
              </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="End attendance">
              <span>
                <IconButton
                  size="small"
                  color="warning"
                  disabled={!isSpecialSessionAttendanceRunning(p?.row)}
                  onClick={async () => {
                    try {
                      await specialSessionService.end(p.row.id);
                      setSnackbar({ open: true, message: "Attendance ended", severity: "success" });
                      loadSessions();
                    } catch (e) {
                      setSnackbar({ open: true, message: e?.response?.data?.message || "Failed to end", severity: "error" });
                    }
                  }}
                >
                  <StopCircle fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Edit">
              <IconButton size="small" color="primary" onClick={() => openEdit(p.row)}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" onClick={() => openDelete(p.row)}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Add external participant">
              <span>
                <IconButton
                  size="small"
                  disabled={!p?.row?.allowExternalParticipants || !p?.row?.manualEnabled}
                  onClick={() => {
                    setActiveSessionForExternal(p.row);
                    setExternalForm({
                      attendeeName: "",
                      attendeeEmail: "",
                      attendeePhone: "",
                      attendeeCompany: "",
                      attendeeDesignation: "",
                      attendeeNotes: "",
                      attendeeType: "outsider",
                    });
                    setAddExternalOpen(true);
                  }}
                >
                  <PersonAdd fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const onCloseCreate = () => {
    setCreateOpen(false);
    setFormErrors({});
  };

  const onOpenCreate = () => {
    setError(null);
    setFormErrors({});
    setClassDeptScope("");
    setCreateOpen(true);
  };

  // Changing the department scope re-scopes the class options. Clear any selected
  // classes that fall outside the newly chosen department so a stale cross-department
  // class isn't submitted (guarded against non-array class state).
  const onChangeClassDeptScope = (nextDeptId) => {
    setClassDeptScope(nextDeptId);
    setForm((p) => {
      const selected = Array.isArray(p.classIds) ? p.classIds : [];
      if (nextDeptId === "" || nextDeptId == null) return { ...p, classIds: selected };
      const list = Array.isArray(classes) ? classes : [];
      const retained = selected.filter((id) => {
        const cls = list.find((c) => Number(c?.id) === Number(id));
        const classDeptId = cls?.departmentId || cls?.department?.id;
        return Number(classDeptId) === Number(nextDeptId);
      });
      return { ...p, classIds: retained };
    });
  };

  const onCreate = async () => {
    const errors = validateSessionForm(form);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;

    setLoading(true);
    setError(null);
    try {
      const { status: _omitStatus, customSessionType: _omitCustom, ...formWithoutStatus } = form;
      const startAt = parseDateTimeLocalAsIST(form.startDateTime);
      const endAt = parseDateTimeLocalAsIST(form.endDateTime);
      const payload = {
        ...formWithoutStatus,
        sessionType: buildSessionTypePayload(form),
        startDateTime: startAt?.toISOString(),
        endDateTime: endAt?.toISOString(),
        departmentIds: (form.departmentIds || []).map((x) => Number(x)),
        classIds: (form.classIds || []).map((x) => Number(x)),
      };
      await specialSessionService.create(payload);
      setSnackbar({ open: true, message: "Created special session", severity: "success" });
      setCreateOpen(false);
      setForm((prev) => ({ ...prev, title: "", description: "" }));
      loadSessions(0, pageSize);
      setPage(0);
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to create session");
    } finally {
      setLoading(false);
    }
  };

  const onUpdate = async () => {
    if (!selectedSession?.id) return;
    setLoading(true);
    setError(null);
    try {
      const { status: _omitStatus, customSessionType: _omitCustom, ...formWithoutStatus } = form;
      const startAt = parseDateTimeLocalAsIST(form.startDateTime);
      const endAt = parseDateTimeLocalAsIST(form.endDateTime);
      const payload = {
        ...formWithoutStatus,
        sessionType: buildSessionTypePayload(form),
        startDateTime: startAt?.toISOString(),
        endDateTime: endAt?.toISOString(),
        departmentIds: (form.departmentIds || []).map((x) => Number(x)),
        classIds: (form.classIds || []).map((x) => Number(x)),
      };
      await specialSessionService.update(selectedSession.id, payload);
      setSnackbar({ open: true, message: "Updated session", severity: "success" });
      setEditOpen(false);
      loadSessions();
    } catch (e) {
      setError(e?.response?.data?.message || "Failed to update");
    } finally {
      setLoading(false);
    }
  };

  const onDelete = async () => {
    if (!selectedSession?.id) return;
    setLoading(true);
    try {
      // backend enforces confirm for completed or if attendance exists
      await specialSessionService.remove(selectedSession.id, { confirm: "true" });
      setSnackbar({ open: true, message: "Deleted session", severity: "success" });
      setDeleteOpen(false);
      loadSessions(0, pageSize);
      setPage(0);
    } catch (e) {
      setSnackbar({ open: true, severity: "error", message: e?.response?.data?.message || "Failed to delete" });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700 }}>
                Special Sessions (Event Attendance)
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create workshops / guest lectures / institute-wide sessions without timetable dependency.
              </Typography>
            </Box>
            <Button variant="contained" startIcon={<Add />} onClick={onOpenCreate}>
              Create Session
            </Button>
          </Box>

          {error ? (
            <Box sx={{ mt: 2 }}>
              <Alert severity="error">{error}</Alert>
            </Box>
          ) : null}

          {/* Filters & Search */}
          <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
            <TextField
              size="small"
              placeholder="Search by title..."
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              sx={{ minWidth: 220 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchQuery ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => { setSearchQuery(""); setPage(0); }}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="ACTIVE">Active</MenuItem>
                <MenuItem value="COMPLETED">Completed</MenuItem>
                <MenuItem value="UPCOMING">Upcoming</MenuItem>
                <MenuItem value="NOT_TAKEN">Not Taken</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Type</InputLabel>
              <Select
                value={typeFilter}
                label="Type"
                onChange={(e) => { setTypeFilter(e.target.value); setPage(0); }}
              >
                <MenuItem value="">All Types</MenuItem>
                {SESSION_TYPES.filter((t) => t !== "Custom Type").map((t) => (
                  <MenuItem key={t} value={t}>{t}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <DatePicker
              label="Date"
              value={dateFilter}
              onChange={(val) => { setDateFilter(val); setPage(0); }}
              slotProps={{
                textField: { size: "small", sx: { minWidth: 160 } },
                actionBar: { actions: ["clear"] },
              }}
            />
            {(searchQuery || statusFilter || typeFilter || dateFilter) ? (
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setSearchQuery("");
                  setStatusFilter("");
                  setTypeFilter("");
                  setDateFilter(null);
                  setPage(0);
                }}
              >
                Clear All
              </Button>
            ) : null}
          </Box>

          <Box sx={{ mt: 2, height: 560 }}>
            <DataGrid
              rows={rows}
              columns={columns}
              loading={loading}
              pagination
              paginationMode="server"
              rowCount={total}
              page={page}
              pageSize={pageSize}
              onPageChange={(p) => setPage(p)}
              onPageSizeChange={(s) => {
                setPageSize(s);
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50]}
              disableSelectionOnClick
              getRowId={(r) => r.id}
            />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={createOpen}
        onClose={(_, reason) => {
          if (reason === "backdropClick") return;
          onCloseCreate();
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Create Special Session</DialogTitle>
        <DialogContent dividers>
          {formErrors.modes ? <Alert severity="error" sx={{ mb: 2 }}>{formErrors.modes}</Alert> : null}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="Title"
                value={form.title}
                onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                required
                error={!!formErrors.title}
                helperText={formErrors.title || ""}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Session Type</InputLabel>
                <Select
                  label="Session Type"
                  value={form.sessionType}
                  onChange={(e) => setForm((p) => ({ ...p, sessionType: e.target.value }))}
                >
                  {SESSION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {form.sessionType === "Custom Type" ? (
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Custom Session Type *"
                  value={form.customSessionType}
                  onChange={(e) => setForm((p) => ({ ...p, customSessionType: e.target.value }))}
                  required
                  error={!!formErrors.customSessionType}
                  helperText={formErrors.customSessionType || "Enter the custom session type name"}
                />
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Description"
                value={form.description}
                onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Host Type</InputLabel>
                <Select
                  label="Host Type"
                  value={form.hostType}
                  onChange={(e) => setForm((p) => ({ ...p, hostType: e.target.value }))}
                >
                  <MenuItem value="internal">Internal</MenuItem>
                  <MenuItem value="external">External</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Host Name"
                value={form.hostName}
                onChange={(e) => setForm((p) => ({ ...p, hostName: e.target.value }))}
                placeholder={form.hostType === "external" ? "Guest speaker name" : "Optional"}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Venue"
                value={form.venue}
                onChange={(e) => setForm((p) => ({ ...p, venue: e.target.value }))}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              <FormControl fullWidth required>
                <InputLabel>Session Mode</InputLabel>
                <Select
                  label="Session Mode"
                  value={form.sessionMode}
                  onChange={(e) => setForm((p) => ({ ...p, sessionMode: e.target.value }))}
                >
                  <MenuItem value="offline">Offline</MenuItem>
                  <MenuItem value="online">Online</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Date/Time"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.startDateTime}
                onChange={(e) => setForm((p) => ({ ...p, startDateTime: e.target.value }))}
                required
                error={!!formErrors.startDateTime}
                helperText={formErrors.startDateTime || ""}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Date/Time"
                type="datetime-local"
                InputLabelProps={{ shrink: true }}
                value={form.endDateTime}
                onChange={(e) => setForm((p) => ({ ...p, endDateTime: e.target.value }))}
                required
                error={!!formErrors.endDateTime}
                helperText={formErrors.endDateTime || ""}
              />
            </Grid>

            {/* Geo fencing fields intentionally removed for special sessions */}

            {/* Department scope for the Classes audience picker. Narrows the Classes
                options to one department so admins/super-admins aren't shown an
                unbounded all-department list (Req 4.1-4.3). Client-side filter over
                the already-loaded classes (each class carries departmentId). */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={lookupsLoading}>
                <InputLabel>Department (scope classes)</InputLabel>
                <Select
                  label="Department (scope classes)"
                  value={classDeptScope}
                  onChange={(e) => onChangeClassDeptScope(e.target.value)}
                >
                  <MenuItem value="">All departments</MenuItem>
                  {(Array.isArray(departments) ? departments : []).map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      {formatDepartmentLabel(d)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={lookupsLoading}>
                <InputLabel>Departments (optional)</InputLabel>
                <Select
                  multiple
                  label="Departments (optional)"
                  value={form.departmentIds}
                  onChange={(e) => setForm((p) => ({ ...p, departmentIds: e.target.value }))}
                  renderValue={(selected) =>
                    (selected || [])
                      .map((id) => formatDepartmentLabel(departments.find((d) => Number(d.id) === Number(id))) || id)
                      .join(", ")
                  }
                >
                  {(Array.isArray(departments) ? departments : []).map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      <Checkbox checked={(form.departmentIds || []).some((x) => Number(x) === Number(d.id))} />
                      <ListItemText primary={formatDepartmentLabel(d)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={lookupsLoading}>
                <InputLabel>Classes (optional)</InputLabel>
                <Select
                  multiple
                  label="Classes (optional)"
                  value={form.classIds}
                  onChange={(e) => setForm((p) => ({ ...p, classIds: e.target.value }))}
                  renderValue={(selected) =>
                    (selected || [])
                      .map((id) => formatClassLabel(classes.find((c) => Number(c.id) === Number(id))) || id)
                      .join(", ")
                  }
                >
                  {(Array.isArray(filteredClasses) ? filteredClasses : []).map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={(form.classIds || []).some((x) => Number(x) === Number(c.id))} />
                      <ListItemText primary={formatClassLabel(c)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {form.sessionType === "Custom Type" ? (
              <Grid item xs={12} md={8}>
                <TextField
                  fullWidth
                  label="Custom Session Type *"
                  value={form.customSessionType}
                  onChange={(e) => setForm((p) => ({ ...p, customSessionType: e.target.value }))}
                  required
                  error={!!formErrors.customSessionType}
                  helperText={formErrors.customSessionType || "Enter the custom session type name"}
                />
              </Grid>
            ) : null}

            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.allowExternalParticipants}
                    onChange={(e) => setForm((p) => ({ ...p, allowExternalParticipants: e.target.checked }))}
                  />
                }
                label="Allow external participants (no ERP account)"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch checked={form.qrEnabled} onChange={(e) => setForm((p) => ({ ...p, qrEnabled: e.target.checked }))} />}
                label="Enable QR"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch checked={form.otpEnabled} onChange={(e) => setForm((p) => ({ ...p, otpEnabled: e.target.checked }))} />}
                label="Enable OTP"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={<Switch checked={form.manualEnabled} onChange={(e) => setForm((p) => ({ ...p, manualEnabled: e.target.checked }))} />}
                label="Enable Manual Attendance"
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.attendanceRequired}
                    onChange={(e) => setForm((p) => ({ ...p, attendanceRequired: e.target.checked }))}
                  />
                }
                label="Attendance required"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseCreate} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={onCreate} variant="contained" disabled={loading || lookupsLoading}>
            {loading ? <CircularProgress size={18} /> : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Special Session</DialogTitle>
        <DialogContent dividers>
          {/* Reuse same form fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField fullWidth label="Title" value={form.title} onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))} required />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Session Type</InputLabel>
                <Select label="Session Type" value={form.sessionType} onChange={(e) => setForm((p) => ({ ...p, sessionType: e.target.value }))}>
                  {SESSION_TYPES.map((t) => (
                    <MenuItem key={t} value={t}>
                      {t}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline minRows={2} label="Description" value={form.description} onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel>Host Type</InputLabel>
                <Select label="Host Type" value={form.hostType} onChange={(e) => setForm((p) => ({ ...p, hostType: e.target.value }))}>
                  <MenuItem value="internal">Internal</MenuItem>
                  <MenuItem value="external">External</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Host Name" value={form.hostName} onChange={(e) => setForm((p) => ({ ...p, hostName: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField fullWidth label="Venue" value={form.venue} onChange={(e) => setForm((p) => ({ ...p, venue: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="Start Date/Time" type="datetime-local" InputLabelProps={{ shrink: true }} value={form.startDateTime} onChange={(e) => setForm((p) => ({ ...p, startDateTime: e.target.value }))} required />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth label="End Date/Time" type="datetime-local" InputLabelProps={{ shrink: true }} value={form.endDateTime} onChange={(e) => setForm((p) => ({ ...p, endDateTime: e.target.value }))} required />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={lookupsLoading}>
                <InputLabel>Departments (optional)</InputLabel>
                <Select
                  multiple
                  label="Departments (optional)"
                  value={form.departmentIds}
                  onChange={(e) => setForm((p) => ({ ...p, departmentIds: e.target.value }))}
                  renderValue={(selected) =>
                    (selected || [])
                      .map((id) => formatDepartmentLabel(departments.find((d) => Number(d.id) === Number(id))) || id)
                      .join(", ")
                  }
                >
                  {departments.map((d) => (
                    <MenuItem key={d.id} value={d.id}>
                      <Checkbox checked={(form.departmentIds || []).some((x) => Number(x) === Number(d.id))} />
                      <ListItemText primary={formatDepartmentLabel(d)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={lookupsLoading}>
                <InputLabel>Classes (optional)</InputLabel>
                <Select
                  multiple
                  label="Classes (optional)"
                  value={form.classIds}
                  onChange={(e) => setForm((p) => ({ ...p, classIds: e.target.value }))}
                  renderValue={(selected) =>
                    (selected || [])
                      .map((id) => formatClassLabel(classes.find((c) => Number(c.id) === Number(id))) || id)
                      .join(", ")
                  }
                >
                  {filteredClasses.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      <Checkbox checked={(form.classIds || []).some((x) => Number(x) === Number(c.id))} />
                      <ListItemText primary={formatClassLabel(c)} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel control={<Switch checked={form.allowExternalParticipants} onChange={(e) => setForm((p) => ({ ...p, allowExternalParticipants: e.target.checked }))} />} label="Allow external participants" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={form.qrEnabled} onChange={(e) => setForm((p) => ({ ...p, qrEnabled: e.target.checked }))} />} label="Enable QR" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={form.otpEnabled} onChange={(e) => setForm((p) => ({ ...p, otpEnabled: e.target.checked }))} />} label="Enable OTP" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={form.manualEnabled} onChange={(e) => setForm((p) => ({ ...p, manualEnabled: e.target.checked }))} />} label="Enable Manual Attendance" />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControlLabel control={<Switch checked={form.attendanceRequired} onChange={(e) => setForm((p) => ({ ...p, attendanceRequired: e.target.checked }))} />} label="Attendance required" />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={onUpdate} variant="contained" disabled={loading || !form.title || !form.startDateTime || !form.endDateTime}>
            {loading ? <CircularProgress size={18} /> : "Save"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Session</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning">
            If attendance exists or session is completed, the server requires confirmation. This will delete the session (soft delete) after confirmation.
          </Alert>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2">
              <b>{selectedSession?.title}</b> (ID: {selectedSession?.id})
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button onClick={onDelete} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={18} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={startModalOpen} onClose={() => setStartModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Attendance Started</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary">
            {startModalData?.title ? startModalData.title : `Session ID: ${startModalData?.sessionId || "-"}`}
          </Typography>

          {startModalData?.qrPublicId ? (
            <Box sx={{ mt: 2, textAlign: "center" }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                QR Code
              </Typography>
              <Box sx={{ display: "inline-block", p: 2, bgcolor: "white", borderRadius: 2, boxShadow: 2 }}>
                <QRCode value={String(startModalData.qrPublicId)} size={220} />
              </Box>
              <Typography variant="body2" sx={{ mt: 1 }}>
                Public ID: <b>{startModalData.qrPublicId}</b>
              </Typography>
            </Box>
          ) : (
            <Alert sx={{ mt: 2 }} severity="info">
              QR is not enabled for this session.
            </Alert>
          )}

          {startModalData?.otpCode ? (
            <Box sx={{ mt: 3, textAlign: "center" }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 700 }}>
                OTP Code
              </Typography>
              <Box sx={{ display: "inline-block", p: 2, bgcolor: "grey.100", borderRadius: 2 }}>
                <Typography variant="h5" sx={{ fontFamily: "monospace", fontWeight: 800, letterSpacing: 2 }}>
                  {startModalData.otpCode}
                </Typography>
              </Box>
              {startModalData.otpExpiresAt ? (
                <Typography variant="caption" display="block" sx={{ mt: 0.5 }} color="text.secondary">
                  Expires at: {new Date(startModalData.otpExpiresAt).toLocaleString()}
                </Typography>
              ) : null}
            </Box>
          ) : (
            <Alert sx={{ mt: 2 }} severity="info">
              OTP is not enabled for this session.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStartModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={modesDialogOpen} onClose={() => setModesDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Session Tags</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {(modesDialogItems || []).map((t) => (
              <Chip key={t} size="small" label={t} />
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={addExternalOpen} onClose={() => setAddExternalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add External Participant</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Name *"
                value={externalForm.attendeeName}
                onChange={(e) => setExternalForm((p) => ({ ...p, attendeeName: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Email (optional)"
                value={externalForm.attendeeEmail}
                error={!!externalErrors.attendeeEmail}
                helperText={externalErrors.attendeeEmail || "Optional"}
                onChange={(e) => {
                  const v = e.target.value;
                  setExternalForm((p) => ({ ...p, attendeeEmail: v }));
                  setExternalErrors((p) => ({ ...p, attendeeEmail: isValidEmail(v) ? "" : "Invalid email" }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Phone (optional)"
                value={externalForm.attendeePhone}
                error={!!externalErrors.attendeePhone}
                helperText={externalErrors.attendeePhone || "Optional (7–15 digits)"}
                onChange={(e) => {
                  const v = e.target.value;
                  setExternalForm((p) => ({ ...p, attendeePhone: v }));
                  setExternalErrors((p) => ({ ...p, attendeePhone: isValidPhone(v) ? "" : "Invalid phone number" }));
                }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Organization / Company (optional)"
                value={externalForm.attendeeCompany}
                onChange={(e) => setExternalForm((p) => ({ ...p, attendeeCompany: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Designation (optional)"
                value={externalForm.attendeeDesignation}
                onChange={(e) => setExternalForm((p) => ({ ...p, attendeeDesignation: e.target.value }))}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Notes (optional)"
                value={externalForm.attendeeNotes}
                onChange={(e) => setExternalForm((p) => ({ ...p, attendeeNotes: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddExternalOpen(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={
              loading ||
              !externalForm.attendeeName ||
              !activeSessionForExternal?.id ||
              !isValidEmail(externalForm.attendeeEmail) ||
              !isValidPhone(externalForm.attendeePhone)
            }
            onClick={async () => {
              setLoading(true);
              try {
                await specialSessionService.manualMark(activeSessionForExternal.id, {
                  ...externalForm,
                  attendeeType: "outsider",
                });
                setSnackbar({ open: true, message: "Marked present", severity: "success" });
                setAddExternalOpen(false);
                setExternalErrors({ attendeeEmail: "", attendeePhone: "" });
              } catch (e) {
                setSnackbar({ open: true, message: e?.response?.data?.message || "Failed", severity: "error" });
              } finally {
                setLoading(false);
              }
            }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((p) => ({ ...p, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SpecialSessionsManagement;

